import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  getCampaignDetails,
  retryBulkCampaignMessages,
  stopBulkCampaign,
  pauseBulkCampaign,
  resumeBulkCampaign,
  rescheduleBulkCampaign,
} from '../api';
import { CampaignLogsTable } from '../components/CampaignLogsTable';
import { CampaignSummary } from '../components/CampaignSummary';
import { StopFollowUpConfirmModal } from '../components/StopFollowUpConfirmModal';
import { ActionConfirmModal } from '../components/ActionConfirmModal';
import { RescheduleModal } from '../crm/components/RescheduleModal';
import { DashboardPage } from '../components/PremiumDesign';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';

/** Statuses where stop / pause still make sense */
const ACTIVE_STATUSES = new Set([
  'pending',
  'processing',
  'scheduled',
  'interval',
  'active',
  'running',
]);

const SCHEDULED_TYPES = new Set(['schedule_once', 'scheduled', 'schedule']);

const decodeId = (encoded) => {
  if (!encoded) return '';
  try {
    return atob(encoded);
  } catch {
    return encoded;
  }
};

const getApiErrorMessage = (err, fallback) =>
  err?.response?.data?.detail ||
  err?.response?.data?.error ||
  err?.response?.data?.code ||
  fallback;

export function CampaignLogPage() {
  const { campaignId: encodedCampaignId } = useParams();
  const campaignId = decodeId(encodedCampaignId);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [actionPopup, setActionPopup] = useState(null);

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();

  const {
    data: campaignDetails,
    isLoading: isDetailsApiLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['bulk-campaign-details', companyId, campaignId],
    queryFn: () => getCampaignDetails({ companyId, campaignId }),
    enabled: !!campaignId,
  });

  const isDetailsLoading = useDelayedSkeleton(isDetailsApiLoading);

  useEffect(() => {
    if (isError) {
      toast.error(getApiErrorMessage(error, 'Failed to load campaign details'));
    }
  }, [isError, error]);

  const invalidateCampaignQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ['bulk-campaign-details', companyId, campaignId],
    });
    queryClient.invalidateQueries({
      queryKey: ['bulk-campaign-logs', companyId, campaignId],
    });
    queryClient.invalidateQueries({ queryKey: ['bulk-campaign-campaigns'] });
    queryClient.invalidateQueries({ queryKey: ['recent-campaigns'] });
  };

  const retryMutation = useMutation({
    mutationFn: () => retryBulkCampaignMessages({ companyId, campaignId }),
    onSuccess: () => {
      toast.success('Retry initiated successfully');
      invalidateCampaignQueries();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to retry messages'));
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => stopBulkCampaign({ companyId, campaignId }),
    onSuccess: () => {
      toast.success('Campaign stopped successfully');
      setActionPopup(null);
      invalidateCampaignQueries();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to stop campaign'));
    },
  });

  const pauseMutation = useMutation({
    mutationFn: () => pauseBulkCampaign({ companyId, campaignId }),
    onSuccess: () => {
      toast.success('Campaign paused successfully');
      setActionPopup(null);
      invalidateCampaignQueries();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to pause campaign'));
    },
  });

  const resumeMutation = useMutation({
    mutationFn: () => resumeBulkCampaign({ companyId, campaignId }),
    onSuccess: () => {
      toast.success('Campaign resumed successfully');
      setActionPopup(null);
      invalidateCampaignQueries();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to resume campaign'));
    },
  });

  const rescheduleMutation = useMutation({
    mutationFn: (newDateTime) =>
      rescheduleBulkCampaign({ companyId, campaignId, newDateTime }),
    onSuccess: () => {
      toast.success('Campaign rescheduled successfully');
      setActionPopup(null);
      invalidateCampaignQueries();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to reschedule campaign'));
    },
  });

  const handleBackToList = () => {
    if (location.state?.fromDashboard) {
      navigate('/dashboard');
    } else {
      navigate('/campaigns');
    }
  };

  const campaignStatus = String(campaignDetails?.status || '').toLowerCase();
  const scheduleType = String(
    campaignDetails?.schedule_details?.schedule_type ||
      campaignDetails?.sending_mode ||
      '',
  ).toLowerCase();
  const scheduleStatus = String(
    campaignDetails?.schedule_details?.schedule_status || '',
  ).toLowerCase();

  const isInterval = scheduleType === 'interval';
  const isScheduled = SCHEDULED_TYPES.has(scheduleType);

  const TERMINAL_STATUSES = new Set([
    'stopped',
    'completed',
    'failed',
    'cancelled',
    'canceled',
  ]);
  const isPaused = campaignStatus === 'paused' || scheduleStatus === 'paused';
  const isTerminal =
    TERMINAL_STATUSES.has(campaignStatus) ||
    TERMINAL_STATUSES.has(scheduleStatus);

  // Interval: Pause / Resume only (never Reschedule or Stop)
  const canPause = isInterval && !isPaused && !isTerminal;
  const canResume = isInterval && isPaused;
  const canStop =
    isScheduled &&
    !isInterval &&
    !isTerminal &&
    (ACTIVE_STATUSES.has(campaignStatus) ||
      isPaused ||
      scheduleStatus === 'active');
  // One-time scheduled only — never when schedule_type is interval
  const canReschedule =
    isScheduled &&
    !isInterval &&
    (ACTIVE_STATUSES.has(campaignStatus) ||
      campaignStatus === 'stopped' ||
      campaignStatus === 'failed');

  const hasScheduleDetails = Boolean(campaignDetails?.schedule_details);

  const actionProps = {
    canRetry: campaignDetails?.failed_count > 0,
    onRetry: () => retryMutation.mutate(),
    isRetrying: retryMutation.isPending,
    canStop,
    onStop: () => setActionPopup('stop'),
    isStopping: stopMutation.isPending,
    canPause,
    onPause: () => setActionPopup('pause'),
    isPausing: pauseMutation.isPending,
    canResume,
    onResume: () => setActionPopup('resume'),
    isResuming: resumeMutation.isPending,
    canReschedule,
    onReschedule: () => setActionPopup('reschedule'),
    isRescheduling: rescheduleMutation.isPending,
  };

  return (
    <DashboardPage className="!p-0 !bg-transparent">
      <div className="w-full min-w-0 rounded-2xl bg-layer-5-card p-3 sm:p-4 flex flex-col gap-3 sm:gap-4 text-left">
        {hasScheduleDetails && (
          <div className="shrink-0 flex flex-col gap-3 sm:gap-4">
            <CampaignSummary
              campaign={campaignDetails}
              isLoading={isDetailsLoading}
              onBack={handleBackToList}
              {...actionProps}
            />
            <div className="border-t border-border-default" />
          </div>
        )}
        <CampaignLogsTable
          companyId={companyId}
          campaignId={campaignId}
          embedded
          onBack={hasScheduleDetails ? undefined : handleBackToList}
          showCampaignActions={!hasScheduleDetails}
          isDetailsLoading={isDetailsLoading || !campaignDetails}
          {...(hasScheduleDetails ? {} : actionProps)}
        />
      </div>

      <StopFollowUpConfirmModal
        isOpen={actionPopup === 'stop'}
        onCancel={() => {
          if (!stopMutation.isPending) setActionPopup(null);
        }}
        onConfirm={() => stopMutation.mutate()}
        isPending={stopMutation.isPending}
        title="Stop Campaign"
        entityLabel="Campaign"
        description="This will permanently cancel the campaign and all pending schedules. This action cannot be undone."
      />

      <RescheduleModal
        isOpen={actionPopup === 'reschedule'}
        onClose={() => {
          if (!rescheduleMutation.isPending) setActionPopup(null);
        }}
        onReschedule={(newDateTime) => rescheduleMutation.mutate(newDateTime)}
        isRescheduling={rescheduleMutation.isPending}
        title="Reschedule Campaign"
        description="Select a new date and time to run this campaign."
      />

      <ActionConfirmModal
        isOpen={actionPopup === 'pause'}
        onCancel={() => {
          if (!pauseMutation.isPending) setActionPopup(null);
        }}
        onConfirm={() => pauseMutation.mutate()}
        isPending={pauseMutation.isPending}
        title="Pause Campaign"
        description="This campaign will pause until you resume it."
        confirmLabel="Pause"
        pendingLabel="Pausing..."
      />

      <ActionConfirmModal
        isOpen={actionPopup === 'resume'}
        onCancel={() => {
          if (!resumeMutation.isPending) setActionPopup(null);
        }}
        onConfirm={() => resumeMutation.mutate()}
        isPending={resumeMutation.isPending}
        title="Resume Campaign"
        description="This campaign will start running again on its schedule."
        confirmLabel="Resume"
        pendingLabel="Resuming..."
      />
    </DashboardPage>
  );
}
